import {
  getAllDocumentationRequiredByLoanId,
  getBorrowerDocumentationByLoanIdAndBorrowerId,
  getCashInCashOutMethodById,
  getIncomeById,
  getKycByExternalId,
  getLoanApplicationById,
  getLoanApplicationsByLoanId,
  getLoanById,
  getLoanStatusesByLoanId,
  getPaymentPlanByExternalId,
  Loan,
  RealTimeEvent,
  RealTimeEventType,
  RealTimeResource,
} from '@nemuru-eng/js-api-client';
import { useEffect, useRef } from 'react';
import { unstable_serialize, useSWRConfig } from 'swr';

import { usePageContext } from '@/contexts/PageContext/PageContext';
import { useRealTimeResource } from '@/hooks/useRealTimeResource';

/** A part of the loan detail that can be refreshed on its own. */
enum LiveLoanTarget {
  LOAN = 'loan',
  KYC = 'kyc',
  LOAN_APPLICATIONS = 'loan_applications',
  DOCUMENTATION = 'documentation',
  PAYMENT_PLAN = 'payment_plan',
  CASH_IN_CASH_OUT_METHOD = 'cash_in_cash_out_method',
  INCOME = 'income',
}

const ALL_TARGETS = Object.values(LiveLoanTarget);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RequestFunction = (params: any) => Promise<unknown>;

/** What each event of the loan topic changes. Unknown types (and `connection.resumed`) refresh everything. */
const TARGETS_BY_EVENT: Partial<Record<RealTimeEventType, LiveLoanTarget[]>> = {
  'loan.status_was_changed': [
    LiveLoanTarget.LOAN,
    LiveLoanTarget.LOAN_APPLICATIONS,
    LiveLoanTarget.DOCUMENTATION,
    LiveLoanTarget.PAYMENT_PLAN,
  ],
  'loan.documentation_was_changed': [LiveLoanTarget.DOCUMENTATION],
  'loan.participants_were_changed': [LiveLoanTarget.LOAN, LiveLoanTarget.KYC],
  'loan.onboarding_details_were_changed': [LiveLoanTarget.LOAN, LiveLoanTarget.KYC, LiveLoanTarget.INCOME],
  'loan.cash_in_cash_out_method_was_changed': [LiveLoanTarget.LOAN, LiveLoanTarget.CASH_IN_CASH_OUT_METHOD],
  'loan.declared_income_was_changed': [LiveLoanTarget.INCOME],
  // The loan embeds the selected application (`loanApplicationSelected`), so its status changes the loan too.
  'loan.loan_application_lender_fields_were_changed': [LiveLoanTarget.LOAN, LiveLoanTarget.LOAN_APPLICATIONS],
};

/** Events arrive in bursts (an onboarding step raises several): refresh once per burst. */
const COALESCE_MS = 300;

/**
 * Keeps the loan detail up to date from the loan's real-time topic, refetching only what each event changed.
 * Mount it once per page: the SWR caches it refreshes are shared by every section.
 *
 * Cache keys follow `useCache`: `[requestFunction.name, ...Object.values(params)]`. Always read the name from the
 * function itself: the production build minifies function names, so a hardcoded 'getLoanById' never matches.
 */
export const useLiveLoan = (loanId: string | undefined) => {
  const { cache, mutate } = useSWRConfig();
  const { actions } = usePageContext();

  const pendingTargets = useRef(new Set<LiveLoanTarget>());
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const refreshing = useRef(false);

  const isKeyOf = (request: RequestFunction, key: unknown) => Array.isArray(key) && key[0] === request.name;

  const revalidateRequest = (request: RequestFunction, matches: (key: unknown[]) => boolean = () => true) =>
    mutate((key) => isKeyOf(request, key) && matches(key as unknown[]));

  const refreshTargets = async (targets: Set<LiveLoanTarget>) => {
    if (!loanId) return;

    // The loan and the KYC drive the page loading layer when revalidated: fetch them and write the cache
    // without revalidating, so the page is not hidden behind the loading layer on every event.
    let loan = cache.get(unstable_serialize([getLoanById.name, loanId]))?.data as Loan | undefined;
    if (targets.has(LiveLoanTarget.LOAN)) {
      loan = await getLoanById({ loanId });
      await mutate([getLoanById.name, loanId], loan, false);
    }
    if (targets.has(LiveLoanTarget.KYC)) {
      await mutate([getKycByExternalId.name, loanId], await getKycByExternalId({ loanId }), false);
    }

    const revalidations: Promise<unknown>[] = [];
    if (targets.has(LiveLoanTarget.LOAN_APPLICATIONS)) {
      revalidations.push(
        revalidateRequest(getLoanApplicationById, (key) =>
          Boolean(loan?.loanApplicationIds?.some(({ value }) => value === key[1])),
        ),
        revalidateRequest(getLoanApplicationsByLoanId, (key) => key[1] === loanId),
      );
    }
    if (targets.has(LiveLoanTarget.DOCUMENTATION)) {
      revalidations.push(
        revalidateRequest(getAllDocumentationRequiredByLoanId, (key) => key[1] === loanId),
        revalidateRequest(getBorrowerDocumentationByLoanIdAndBorrowerId, (key) => key[1] === loanId),
      );
    }
    if (targets.has(LiveLoanTarget.LOAN)) {
      // Used by the support and action dialogs; the status timeline (Progress) refetches when the loan changes.
      revalidations.push(revalidateRequest(getLoanStatusesByLoanId, (key) => key[1] === loanId));
    }
    if (targets.has(LiveLoanTarget.PAYMENT_PLAN)) {
      revalidations.push(revalidateRequest(getPaymentPlanByExternalId, (key) => key[1] === loanId));
    }
    if (targets.has(LiveLoanTarget.CASH_IN_CASH_OUT_METHOD)) {
      // A new method id is fetched by its own (new) key once the loan is written; revalidate the current one.
      revalidations.push(
        revalidateRequest(getCashInCashOutMethodById, (key) => key[1] === loan?.borrowerCashInCashOutMethodId),
      );
    }
    if (targets.has(LiveLoanTarget.INCOME)) {
      revalidations.push(revalidateRequest(getIncomeById, (key) => key[1] === loanId));
    }
    await Promise.all(revalidations);
  };

  const flush = async () => {
    if (refreshing.current) return; // the running refresh flushes again when it ends
    refreshing.current = true;
    try {
      while (pendingTargets.current.size > 0) {
        const targets = pendingTargets.current;
        pendingTargets.current = new Set();
        try {
          await refreshTargets(targets);
        } catch {
          // A failed refresh is not fatal: the next event (or a manual refresh) retries.
        }
      }
    } finally {
      refreshing.current = false;
    }
  };

  const onEvent = (event: RealTimeEvent) => {
    (TARGETS_BY_EVENT[event.event] ?? ALL_TARGETS).forEach((target) => pendingTargets.current.add(target));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), COALESCE_MS);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  const { state } = useRealTimeResource({ resource: RealTimeResource.LOAN, id: loanId, onEvent });

  useEffect(() => {
    actions.setLiveState(state);
  }, [state]);

  useEffect(() => () => actions.setLiveState('closed'), []);

  return state;
};
